import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  SelectChangeEvent,
  Grid,
  Paper,
  FormHelperText,
  useTheme
} from '@mui/material';
import MoodIcon from '@mui/icons-material/Mood';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import NatureIcon from '@mui/icons-material/Nature';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

export interface ListenerSettings {
  mood: 'Optimistic' | 'Neutral' | 'Serious';
  soundscape: 'Cityscape' | 'Cafe' | 'Nature' | 'Silence';
  informality: number; // 1 (Formal) to 5 (Friendly)
}

interface ListenerConfigPanelProps {
  settings?: ListenerSettings;
  onChange?: (settings: ListenerSettings) => void;
  disabled?: boolean;
}

const moodOptions = [
  { value: 'Optimistic', icon: <MoodIcon />, label: 'Optimistic' },
  { value: 'Neutral', icon: <SentimentNeutralIcon />, label: 'Neutral' },
  { value: 'Serious', icon: <SentimentVeryDissatisfiedIcon />, label: 'Serious' },
];

const soundscapeOptions = [
  { value: 'Cityscape', icon: <LocationCityIcon />, label: 'Cityscape' },
  { value: 'Cafe', icon: <LocalCafeIcon />, label: 'Cafe' },
  { value: 'Nature', icon: <NatureIcon />, label: 'Nature' },
  { value: 'Silence', icon: <VolumeOffIcon />, label: 'Silence' },
];

// Helper function to compare objects for deep equality
const isEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => isEqual(obj1[key], obj2[key]));
};

const ListenerConfigPanel: React.FC<ListenerConfigPanelProps> = ({
  settings: initialSettings,
  onChange,
  disabled = false
}) => {
  const theme = useTheme();
  const [settings, setSettings] = useState<ListenerSettings>(
    initialSettings || { mood: 'Neutral', soundscape: 'Silence', informality: 5 }
  );
  
  // Add a temporary state for the slider's visual position during dragging
  const [tempInformality, setTempInformality] = useState<number>(
    initialSettings?.informality || 5
  );
  
  // Keep track of whether changes were made by the user or from props
  const [userChangedSettings, setUserChangedSettings] = useState(false);

  // Update internal state when props change, but only if they don't match current state
  // and if the user hasn't just changed the settings (avoiding feedback loops)
  useEffect(() => {
    if (initialSettings && !userChangedSettings && !isEqual(initialSettings, settings)) {
      setSettings(initialSettings);
      setTempInformality(initialSettings.informality);
    }
    // Reset the user change flag after applying prop changes
    if (userChangedSettings) {
      setUserChangedSettings(false);
    }
  }, [initialSettings, userChangedSettings, settings]);

  // Notify parent of changes only when settings actually change
  useEffect(() => {
    if (onChange && userChangedSettings) {
      onChange(settings);
    }
  }, [settings, onChange, userChangedSettings]);

  const handleMoodChange = (
    _: React.MouseEvent<HTMLElement>,
    newMood: ListenerSettings['mood']
  ) => {
    if (newMood) {
      setUserChangedSettings(true);
      const newSettings = { ...settings, mood: newMood };
      setSettings(newSettings);
    }
  };

  const handleSoundscapeChange = (
    event: SelectChangeEvent<ListenerSettings['soundscape']>
  ) => {
    const val = event.target.value as ListenerSettings['soundscape'];
    setUserChangedSettings(true);
    const newSettings = { ...settings, soundscape: val };
    setSettings(newSettings);
  };

  // Update the visual position during drag without triggering parent onChange
  const handleInformalityChange = (
    _: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[]
  ) => {
    const informality = Array.isArray(value) ? value[0] : value;
    setTempInformality(informality);
  };

  // Only commit the change when dragging is complete
  const handleInformalityCommitted = (
    _: Event | React.SyntheticEvent<Element, Event>,
    value: number | number[]
  ) => {
    const informality = Array.isArray(value) ? value[0] : value;
    setUserChangedSettings(true);
    const newSettings = { ...settings, informality };
    setSettings(newSettings);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        backgroundColor: 'action.hover',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Mood / Atmosphere Selection */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Mood / Atmosphere
          </Typography>
          <ToggleButtonGroup
            value={settings.mood}
            exclusive
            onChange={handleMoodChange}
            fullWidth
            disabled={disabled}
          >
            {moodOptions.map(opt => (
              <ToggleButton key={opt.value} value={opt.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: settings.mood === opt.value ? theme.palette.primary.main : 'inherit'
                    }}
                  >
                    {opt.icon}
                  </Avatar>
                  <Typography variant="body2">{opt.label}</Typography>
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <FormHelperText>
            Sets the overall tone and mood of the listening experience
          </FormHelperText>
        </Grid>

        {/* Background Soundscape */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth disabled={disabled}>
            <InputLabel id="soundscape-label">Background Soundscape</InputLabel>
            <Select
              labelId="soundscape-label"
              value={settings.soundscape}
              label="Background Soundscape"
              onChange={handleSoundscapeChange}
            >
              {soundscapeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {opt.icon}
                    <Typography>{opt.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Choose ambient sound for the listening experience
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      {/* Conversational Informality Level */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Conversational Informality
        </Typography>
        <Slider
          min={1}
          max={5}
          step={1}
          value={tempInformality}
          onChange={handleInformalityChange}
          onChangeCommitted={handleInformalityCommitted}
          aria-labelledby="informality-slider"
          valueLabelDisplay="auto"
          disabled={disabled}
          sx={{ mt: 2 }}
        />
        <Box display="flex" justifyContent="space-between" px={2} mt={-1}>
          <Typography variant="caption">Very Formal</Typography>
          <Typography variant="caption">Formal</Typography>
          <Typography variant="caption">Balanced</Typography>
          <Typography variant="caption">Friendly</Typography>
          <Typography variant="caption">Very Friendly</Typography>
        </Box>
        <FormHelperText>
          Adjust how formal or conversational the content should be
        </FormHelperText>
      </Box>
    </Paper>
  );
};

export default ListenerConfigPanel;
